import React, { useState } from "react";
import { Grid, Card, Divider, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import { postNoResponse } from "utils/WebService";
import { useMaterialUIController } from "context";
import MigracaoDeParaDominios from "./MigracaoDeParaDominios";

export default function MigracaoLegado() {
    const [loading, setLoading] = useState(false);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");
    const [idCliente, setIdCliente] = useState("");
    const [dataInicio, setDataInicio] = useState("");
    const [dataFim, setDataFim] = useState("");
    const [minSeqLigacao, setMinSeqLigacao] = useState("");
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const showSnackbar = (msg, isSuccess) => {
        setSbMessage(msg);
        if (isSuccess) setSuccessSB(true);
        else setErrorSB(true);
    };

    const runMigration = async (endpoint, name, extraPayload = {}) => {
        if (!window.confirm(`Deseja iniciar a migração de ${name}?`)) return;
        setLoading(true);
        try {
            const payload = {
                ...(idCliente.trim() ? { idClienteLegado: idCliente.trim() } : {}),
                ...extraPayload
            };
            await postNoResponse(endpoint, payload);
            showSnackbar(`Migração de ${name} concluída com sucesso!`, true);
        } catch (e) {
            showSnackbar(`Erro ao migrar ${name}: ${e.message || 'Erro desconhecido'}`, false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                <Card variant="outlined" sx={{ 
                    p: 3, 
                    mb: 2, 
                    borderColor: "divider" 
                }}>
                    <MDBox>
                        <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>Migração de Dados (Legado)</MDTypography>
                        <MDTypography variant="button" color="text" mb={2}>
                            Execute os passos abaixo sequencialmente para migrar os dados do banco antigo para o novo.
                        </MDTypography>
                        
                        <MDBox mt={3} mb={3} p={3} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                            <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>Filtro de Migração (Opcional)</MDTypography>
                            <MDTypography variant="caption" color="text" display="block" mb={2}>
                                Se preenchido, apenas os dados pertencentes a este Cliente (Código Legado) serão migrados. É recomendado utilizar este filtro para testes isolados antes da virada geral de chave.
                            </MDTypography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <MDInput 
                                        type="number" 
                                        label="ID do Cliente no Legado" 
                                        fullWidth
                                        value={idCliente} 
                                        onChange={(e) => setIdCliente(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </MDBox>

                        <Divider sx={{ my: 3 }} />
                        
                        {loading && (
                            <MDBox display="flex" flexDirection="column" alignItems="center" my={3}>
                                <CircularProgress color="info" />
                                <MDTypography variant="caption" mt={1}>Processando migração. Isso pode demorar...</MDTypography>
                            </MDBox>
                        )}

                        <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>0. Migrar Usuários (Cobradores)</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os usuários/operadores do legado.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/usuarios', 'Usuários (Cobradores)')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>0.5. Migrar Tipos de Ocorrência</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa o cadastro de tipos de ocorrências.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/tipos-ocorrencia', 'Tipos de Ocorrência')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>1. Migrar Clientes (Credores)</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os dados básicos dos credores (TbPessoa).</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/clientes', 'Clientes')}>Executar</MDButton>
                            </MDBox>

                            <MDBox p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MigracaoDeParaDominios showSnackbar={showSnackbar} idCliente={idCliente} />
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>2. Migrar Fichas e Devedores</MDTypography>
                                    <MDTypography variant="caption" color="text">Cria as Fichas (TbDevedorCarteira) e os Devedores (TbDevedor).</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/fichas', 'Fichas')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>3. Migrar Contratos e Dívidas</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os débitos associando-os às fichas geradas no passo anterior.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/contratos', 'Contratos/Dívidas')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>4. Migrar Acordos e Lançamentos</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa acordos firmados e pagamentos (lançamentos).</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/acordos', 'Acordos e Lançamentos')}>Executar</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox>
                                    <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>5. Migrar Boletos Físicos</MDTypography>
                                    <MDTypography variant="caption" color="text">Importa os boletos emitidos e sincroniza a sequência do sistema.</MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/boletos', 'Boletos')}>Executar</MDButton>
                            </MDBox>

                            <MDBox p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>6. Migrar Histórico de Ligações</MDTypography>
                                        <MDTypography variant="caption" color="text">Importa o histórico de cobrança e gravações telefônicas.</MDTypography>
                                    </MDBox>
                                    <MDButton variant="gradient" color="info" disabled={loading} onClick={() => runMigration('/migracao/ligacoes', 'Histórico de Ligações', { dataInicio, dataFim, minSeqLigacao })}>Executar</MDButton>
                                </MDBox>
                                <Grid container spacing={2} mt={1}>
                                    <Grid item xs={12} md={4}>
                                        <MDInput type="date" label="Data Início" fullWidth value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <MDInput type="date" label="Data Fim" fullWidth value={dataFim} onChange={(e) => setDataFim(e.target.value)} InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <MDInput type="number" label="A partir do SeqLigação" fullWidth value={minSeqLigacao} onChange={(e) => setMinSeqLigacao(e.target.value)} />
                                    </Grid>
                                </Grid>
                            </MDBox>
                        </MDBox>
                    </MDBox>
                </Card>
            </Grid>
            
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite={!darkMode}
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite={!darkMode}
            />
        </Grid>
    );
}
